import React, { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddLinkRoundedIcon from "@mui/icons-material/AddLinkRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import CloudOffOutlinedIcon from "@mui/icons-material/CloudOffOutlined";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { brand, notice as tones } from "../../../theme/businessFlowsTheme";

const EDITIONS = { enterprise: "Enterprise", community: "Community" };

// What the search box narrows to - the same rule wherever a database is
// counted or listed, so the page and this list never disagree.
export const matchesTerm = (db, query) => {
  const term = String(query || "").trim().toLowerCase();
  return !term || [db.databaseName, db.key, db.label].filter(Boolean).some((value) => value.toLowerCase().includes(term));
};

// A server can be reachable, away, or answering with credentials that don't
// work - three distinct states, each with its own colour and next step.
const SERVER_STATUS = {
  online: { label: "Online", tone: tones.good },
  credentials: { label: "Credentials don't work", tone: tones.wait, icon: <WarningAmberRoundedIcon /> },
  offline: { label: "Offline", tone: tones.quiet, icon: <CloudOffOutlinedIcon /> },
};

function ServerStatus({ server, mayManage, onEdit }) {
  const look = SERVER_STATUS[server.status] || SERVER_STATUS.offline;
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Chip
        size="small"
        icon={look.icon}
        label={look.label}
        sx={{ height: 22, fontWeight: 650, fontSize: "0.72rem", borderRadius: 1.5, color: look.tone.fg, bgcolor: look.tone.bg, border: `1px solid ${look.tone.line}` }}
      />
      {server.status === "credentials" && mayManage && (
        <Button size="small" onClick={() => onEdit(server)}>
          Edit
        </Button>
      )}
    </Stack>
  );
}

// Every server, each with its databases. Databases the app can open are
// selectable; ones on another server that nobody has connected yet offer
// Connect - and Archive, to stop offering a database this instance has no
// business with. An archived one says so instead.
export default function ServerList({
  servers,
  query,
  selected,
  renderVerdict,
  describe,
  mayManage,
  onSelect,
  onConnect,
  onArchiveDatabase,
  filter,
  onCreate,
  onEdit,
  onArchive,
}) {
  const [menu, setMenu] = useState(null);

  if (!servers) {
    return (
      <Stack spacing={2}>
        {[0, 1].map((i) => (
          <Skeleton key={i} variant="rounded" height={180} sx={{ borderRadius: 4 }} />
        ))}
      </Stack>
    );
  }

  const term = query.trim().toLowerCase();
  // The search box, and whatever the header chips are filtering by.
  const matches = (db) => matchesTerm(db, query) && (!filter || filter(db));

  // Cloud and on-premise are different places with different people looking
  // after them; they are grouped apart whenever both are connected.
  const renderServer = (server) => {
    const databases = server.databases.filter(matches);
    const online = server.status === "online";
    // A filter that leaves a server with nothing takes the server with it,
    // rather than leaving an empty card to read past.
    if (!databases.length && filter) return null;
    return (
      <Paper key={server.id} elevation={0} sx={{ borderRadius: 4, border: `1px solid ${brand.line}`, overflow: "hidden" }}>
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ px: 2, py: 1.5, rowGap: 1, bgcolor: brand.shell }}
        >
          <DnsOutlinedIcon sx={{ color: "text.secondary" }} />
          <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, overflowWrap: "anywhere" }}>
              {server.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", overflowWrap: "anywhere" }}>
              {server.host}
              {server.edition ? ` · ${EDITIONS[server.edition] || server.edition}` : ""}
            </Typography>
          </Box>
          <ServerStatus server={server} mayManage={mayManage} onEdit={onEdit} />
          {mayManage && !server.builtIn && (
            <IconButton size="small" aria-label={`More actions for ${server.name}`} onClick={(e) => setMenu({ anchor: e.currentTarget, server })}>
              <MoreVertRoundedIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>

        {!online && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, pt: 1.5 }}>
            {server.message ||
              (server.status === "credentials"
                ? "Its stored user and password don't work any more."
                : "This server can't be reached right now.")}{" "}
            Its databases can't be opened until this is fixed.
          </Typography>
        )}

        <Stack divider={<Divider />}>
          {databases.map((db) => {
            const openable = Boolean(db.key);
            const active = openable && db.key === selected;
            return (
              <Box
                key={db.databaseName}
                role={openable ? "button" : undefined}
                tabIndex={openable ? 0 : undefined}
                onClick={openable ? () => onSelect(db.key) : undefined}
                onKeyDown={openable ? (e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onSelect(db.key)) : undefined}
                sx={{
                  px: 2,
                  py: 1.25,
                  cursor: openable ? "pointer" : "default",
                  borderLeft: `4px solid ${active ? brand.signal : "transparent"}`,
                  bgcolor: active ? brand.shell : "transparent",
                  "&:hover": openable ? { bgcolor: brand.shell } : undefined,
                  "&:focus-visible": { outline: `2px solid ${brand.ink}`, outlineOffset: -2 },
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 650, overflowWrap: "anywhere" }}>
                      {db.label || db.key || db.databaseName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      {describe(db)}
                    </Typography>
                  </Box>
                  {openable ? (
                    renderVerdict(db.key)
                  ) : mayManage && online ? (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Button size="small" variant="outlined" onClick={() => onConnect(server, db.databaseName)}>
                        Connect
                      </Button>
                      {/* Not every database on a server belongs in Business
                          Flows. Archiving one takes it off this list and
                          changes nothing in Neo4j; it comes back under
                          Archived. */}
                      <Tooltip title="Archive: stop offering it here. Nothing in Neo4j is changed.">
                        <IconButton
                          size="small"
                          aria-label={`Archive ${db.databaseName}`}
                          onClick={() => onArchiveDatabase(db, server)}
                          sx={{ color: "text.secondary" }}
                        >
                          <ArchiveOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ) : (
                    <Chip size="small" label="Not connected" sx={{ height: 22, fontSize: "0.7rem" }} />
                  )}
                </Stack>
              </Box>
            );
          })}
          {online && databases.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
              {term ? "No database here matches your search." : server.listed ? "No databases on this server yet." : "This server doesn't list its databases. Use Connect a database and type its name."}
            </Typography>
          )}
        </Stack>

        {mayManage && online && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ px: 2, py: 1.25, borderTop: `1px solid ${brand.hairline}` }}>
            <Tooltip title={server.canCreate ? "" : "This server doesn't allow creating databases (Neo4j Community edition or AuraDB). Create it on the server, then connect it."}>
              <span>
                <Button size="small" startIcon={<AddRoundedIcon />} disabled={!server.canCreate} onClick={() => onCreate(server)}>
                  Create database
                </Button>
              </span>
            </Tooltip>
            <Button size="small" startIcon={<AddLinkRoundedIcon />} onClick={() => onConnect(server, null)}>
              Connect a database
            </Button>
          </Stack>
        )}
      </Paper>
    );
  };

  const cloud = servers.filter((server) => server.kind === "cloud");
  const onPremise = servers.filter((server) => server.kind !== "cloud");
  const groups = cloud.length && onPremise.length ? [["Cloud", cloud], ["On-premise", onPremise]] : [[null, servers]];

  return (
    <Stack spacing={2}>
      {groups.map(([heading, group]) => (
        <Stack key={heading || "all"} spacing={2}>
          {heading && (
            <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
              {heading}
            </Typography>
          )}
          {group.map(renderServer)}
        </Stack>
      ))}

      <Menu anchorEl={menu?.anchor || null} open={Boolean(menu)} onClose={() => setMenu(null)}>
        <MenuItem
          onClick={() => {
            onEdit(menu.server);
            setMenu(null);
          }}
        >
          <ListItemIcon>
            <EditOutlinedIcon fontSize="small" />
          </ListItemIcon>
          Edit connection
        </MenuItem>
        <MenuItem
          onClick={() => {
            onArchive(menu.server);
            setMenu(null);
          }}
          sx={{ color: tones.fix.fg }}
        >
          <ListItemIcon>
            <ArchiveOutlinedIcon fontSize="small" sx={{ color: tones.fix.fg }} />
          </ListItemIcon>
          Archive server
        </MenuItem>
      </Menu>
    </Stack>
  );
}
